import { JSX, ReactNode, useEffect, useState } from "react";

import { useRootStore } from "store";
import Modal from "components/modal/Modal";
import { Input, Label } from "components/input/Input";
import Link from "components/link/Link";
import { Button, PrimaryButton } from "components/button/Button";
import { Alert } from "components/alert/Alert";
import { Loader } from "components/loader/Loader";
import { LoginMethodAPI } from "api/loginMethod";

import "./LoginForm.scss";

export default function LoginForm() {
  const { supportContactInformation: contactInfo } = useRootStore(
    (state) => state.global.staticInformation
  );
  const { user, error } = useRootStore((state) => state);

  const { appendErrorNotification } = error;

  const { fetchUserLogin } = user;

  const [showModal, setShowModal] = useState(false);
  const [username, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordShowing, setIsPasswordShowing] = useState(false);
  const [hasLoginMethodBeenSelected, setHasLoginMethodBeenSelected] =
    useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [isGettingLoginMethod, setIsGettingLoginMethod] = useState(false);

  useEffect(() => {
    if (user.showNotActivatedMessage) {
      setShowModal(true);
    }
  }, [user.showNotActivatedMessage]);

  const resetPasswordField = () => {
    setIsPasswordShowing(false);
    setHasLoginMethodBeenSelected(false);
  };

  const loginUser = () => {
    fetchUserLogin({
      code: "",
      username: username,
      password: password
    });
  };

  const getUserLoginMethod = async () => {
    setLoginErrorMessage("");
    setIsGettingLoginMethod(true);

    try {
      const response = await LoginMethodAPI.getLoginMethod(username);
      if (response.loginMethod > 0) {
        setHasLoginMethodBeenSelected(true);
      }

      if (response.loginMethod === 0) {
        // None from server, invalid
        setLoginErrorMessage(response.errorMessage ?? "");
      } else if (response.loginMethod === 1) {
        // Username and password
        setIsPasswordShowing(true);
      } else if (response.loginMethod === 2) {
        // Azure AD internal
        setIsPasswordShowing(false);
        let loginUrl = response.loginUrl;
        if (
          process.env.NODE_ENV === "development" ||
          process.env.REACT_APP_STAGE === "qa"
        ) {
          /*
           * When running in development or mittkontor-qa but using test as backend, the test backend will overwrite the redirectUri.
           * This piece of code changes the redirectUri so it still points to localhost or mittkontor-qa, rather than mittkontor-test.ludvig.se.
           */
          const redirectUri =
            window.location.origin +
            "/" +
            loginUrl
              .match(/(redirect_uri=)[^&]+/)?.[0]
              .split("=")[1]
              .split("/")
              .slice(3)
              .join("/");
          loginUrl = loginUrl.replace(
            /(redirect_uri=)[^&]+/,
            "$1" + redirectUri
          );
        }
        window.location.href = loginUrl;
        return;
      }
    } catch (err) {
      appendErrorNotification("Failed to fetch login method", err);
    } finally {
      setIsGettingLoginMethod(false);
    }
  };

  const renderText = (header: string, paragraphs: string[]) => {
    const ps: ReactNode[] = [];
    for (let i = 0; i < paragraphs.length; i++) {
      ps.push(<p key={i}>{paragraphs[i]}</p>);
    }
    return [<h1 key="modH">{header}</h1>, ...ps];
  };

  const renderModal = () => {
    if (!showModal) {
      return;
    }

    return (
      <Modal
        modalTitle="activate account"
        onDismiss={() => setShowModal(false)}
      >
        <div className="modalText">
          {renderText("Aktivera ditt konto", [
            "Du har inte aktiverat ditt konto. Vi har skickat ett nytt aktiveringsmail till din e-postadress.",
            "Får du inget mail? Titta i skräpposten."
          ])}
          <PrimaryButton autoFocus onClick={() => setShowModal(false)}>
            Ok
          </PrimaryButton>
        </div>
      </Modal>
    );
  };

  const renderError = () => {
    let alerts: JSX.Element[] = [];
    if (user.loginFailed) {
      const wrongPasswordAlert = (
        <Alert
          variant="warning"
          className="mb-2"
          key={"wrongPasswordAlert"}
          onDismiss={() => {}}
        >
          <p>Fel e-postadress eller lösenord.</p>
        </Alert>
      );
      const problemsAlert = (
        <Alert
          variant="warning"
          className="mb-4"
          key={"problemsAlert"}
          onDismiss={() => {}}
        >
          <p>
            Har du problem med att logga in? Läs mer under{" "}
            <a
              className="link"
              rel="noreferrer"
              target="_blank"
              href={contactInfo.externalSupportSite}
            >
              Frågor och svar
            </a>{" "}
            om du behöver hjälp.
          </p>
        </Alert>
      );
      alerts = [wrongPasswordAlert, problemsAlert];
    }

    if (user.loginTimeout) {
      alerts.push(
        <Alert
          variant="warning"
          className="mb-4"
          key={"loginTimeoutAlert"}
          onDismiss={() => {}}
        >
          Din session är inte längre giltig, var god logga in igen
        </Alert>
      );
    }

    if (loginErrorMessage) {
      alerts.push(
        <Alert
          variant="warning"
          className="mb-4"
          key={"loginErrorMessageAlert"}
          onDismiss={() => {}}
        >
          {loginErrorMessage}
        </Alert>
      );
    }

    return alerts;
  };

  return (
    <form
      className="loginForm"
      onSubmit={(e) => {
        // If not all fields are set, then do not submit form
        if (!password) {
          e.preventDefault();
        }
        if (hasLoginMethodBeenSelected) {
          loginUser();
        } else {
          getUserLoginMethod();
        }
      }}
    >
      {renderError()}
      {renderModal()}

      <h3>Logga in</h3>

      <Label htmlFor="username">Ange e-postadress</Label>
      <Input
        className="mb-5"
        type="text"
        name="username"
        value={username}
        autoFocus
        onChange={(e) => {
          resetPasswordField();
          setUserName(e.target.value.trim());
        }}
      />
      {isPasswordShowing && (
        <>
          <Label htmlFor="password">Ange lösenord</Label>
          <Input
            className="mb-5"
            type="password"
            name="password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </>
      )}

      <div className="loginFormButtons">
        {hasLoginMethodBeenSelected ? (
          <PrimaryButton
            size="lg"
            disabled={user.isAuthenticating}
            onClick={loginUser}
            type="submit"
          >
            Logga in
          </PrimaryButton>
        ) : (
          <Button
            size="lg"
            onClick={getUserLoginMethod}
            type="button"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
            disabled={isGettingLoginMethod}
          >
            {isGettingLoginMethod && (
              <>
                <Loader style={{ margin: 0 }} />
                &nbsp;
              </>
            )}
            Logga in
          </Button>
        )}
        <div>
          <Link className="link" to="/resetpassword">
            Glömt lösenord?
          </Link>
          <Link className="link" to="/generate-onetime-login">
            Engångslänk för inloggning
          </Link>
        </div>
      </div>
    </form>
  );
}
